import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Badge } from "antd";
import { BellTwoTone } from "@ant-design/icons";
import {
    getStaffNotificationsCount,
    getStaffNotifications,
} from "../../redux/actions/staffNotification/staffNotification";
import { usePushNotifications, requestPushPermission } from "./usePushNotifications";

const INTERVAL_MS = 60000;

const StaffNotifications = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    usePushNotifications();

    const unreadNotifications = useSelector(
        (state) => state.staffNotification?.count?.unreadNotifications ?? 0
    );

    const pollTimeout = useRef(null);
    const unmounted = useRef(false);
    const prevUnread = useRef(null);
    const audio = useRef(null);

    const playSound = useCallback(() => {
        try {
            if (!audio.current) audio.current = new Audio("/notification.mp3");
            audio.current.currentTime = 0;
            audio.current.play().catch(() => { });
        } catch {
            // ignore
        }
    }, []);

    // Play sound when unread count increases
    useEffect(() => {
        if (prevUnread.current !== null && unreadNotifications > prevUnread.current) {
            playSound();
        }
        prevUnread.current = unreadNotifications;
    }, [unreadNotifications, playSound]);

    const scheduleNextPoll = useCallback(() => {
        if (unmounted.current) return;
        if (pollTimeout.current) clearTimeout(pollTimeout.current);
        pollTimeout.current = setTimeout(async () => {
            try {
                await dispatch(getStaffNotificationsCount());
            } finally {
                scheduleNextPoll();
            }
        }, INTERVAL_MS);
    }, [dispatch]);

    const onVisibilityChange = useCallback(() => {
        if (document.hidden) {
            if (pollTimeout.current) clearTimeout(pollTimeout.current);
        } else {
            dispatch(getStaffNotificationsCount());
            scheduleNextPoll();
        }
    }, [dispatch, scheduleNextPoll]);

    useEffect(() => {
        dispatch(getStaffNotificationsCount());
        dispatch(getStaffNotifications());
        scheduleNextPoll();
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            unmounted.current = true;
            if (pollTimeout.current) clearTimeout(pollTimeout.current);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [dispatch, scheduleNextPoll, onVisibilityChange]);

    return (
        <Badge
            className="notification-button"
            count={Number(unreadNotifications || 0)}
            onClick={async () => {
                requestPushPermission().catch((err) => {
                    console.error("[PushNotifications] request failed:", err);
                });
                navigate("/notifications");
            }}
        >
            <BellTwoTone style={{ fontSize: "1.5em" }} />
        </Badge>
    );
};

export default StaffNotifications;